import { useState, useCallback } from 'react';
import { useNotification } from '@hooks/useNotification';
import { useLanguage } from '@contexts/LanguageContext';
import api from '@services/api';

export const useResource = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const notify = useNotification();
    const { t } = useLanguage();

    const fetchResource = useCallback(async (id) => {
        if (!id) return null;

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`/resources/${id}`);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('resourceView.fetchError');
            setError(errorMessage);
            notify.error(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [t, notify]);

    const updateResource = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.put(`/resources/${id}`, data);
            notify.success(t('common.updateSuccess'));
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('common.updateError');
            setError(errorMessage);
            notify.error(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [t, notify]);

    const deleteResource = useCallback(async (id) => {
        setLoading(true);
        setError(null);

        try {
            await api.delete(`/resources/${id}/`);
            notify.success(t('resourceView.deleteSuccess'));
            return true;
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('resourceView.deleteError');
            setError(errorMessage);
            notify.error(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [t, notify]);

    return {
        loading,
        error,
        fetchResource,
        updateResource,
        deleteResource
    };
};